/**
 * Open a popup and print server-rendered HTML (invoices, shipping labels).
 *
 * The HTML the BE returns embeds a remote logo `<img>`. The old call sites
 * triggered `window.print()` on a fixed 250ms timer — a race the logo
 * usually lost on anything but a warm cache: the print snapshot was taken
 * before the image finished downloading, so the logo appeared on screen
 * but came out blank on paper. This waits for every image in the popup to
 * settle (load *or* error) before printing, with a safety cap so a broken
 * image URL can't hang the print dialog forever.
 *
 * @returns `false` if the popup was blocked — the caller shows the toast.
 */
export function printHtmlInWindow(
  html: string,
  opts?: { width?: number; height?: number },
): boolean {
  const width = opts?.width ?? 900;
  const height = opts?.height ?? 1100;

  const w = window.open('', '_blank', `width=${width},height=${height}`);
  if (!w) return false;

  w.document.open();
  w.document.write(html);
  w.document.close();
  w.focus();

  let printed = false;
  const fire = () => {
    if (printed) return;
    printed = true;
    try {
      w.print();
    } catch {
      /* popup is still open — the user can Ctrl+P */
    }
  };

  // `document.images` is populated synchronously once the written markup
  // is parsed. Any image not yet `complete` still has its bytes in flight;
  // wait for each to fire load/error before snapshotting for print.
  const imgs = Array.from(w.document.images);
  const pending = imgs.filter((img) => !img.complete);

  if (pending.length === 0) {
    fire();
    return true;
  }

  let remaining = pending.length;
  const settle = () => {
    remaining -= 1;
    if (remaining <= 0) fire();
  };
  pending.forEach((img) => {
    img.addEventListener('load', settle, { once: true });
    img.addEventListener('error', settle, { once: true });
  });

  // Safety net — never let a stalled or unreachable image block printing.
  window.setTimeout(fire, 4000);
  return true;
}
